import React, { useEffect, useRef, useState } from 'react';
import { TimeOfDay, WeatherType } from '../types';

interface GardenWeatherEffectsProps {
  timeOfDay: TimeOfDay;
  weather: WeatherType;
  onCloudClick?: (e: React.MouseEvent) => void;
}

export const GardenWeatherEffects: React.FC<GardenWeatherEffectsProps> = React.memo(({
  timeOfDay,
  weather,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [lightningFlash, setLightningFlash] = useState(false);

  // Trigger occasional subtle lightning during rainy weather
  useEffect(() => {
    if (weather !== 'rainy') return;

    const interval = setInterval(() => {
      if (document.hidden) return;
      if (Math.random() < 0.25) {
        setLightningFlash(true);
        setTimeout(() => setLightningFlash(false), 90);
        setTimeout(() => {
          if (Math.random() < 0.5) {
            setLightningFlash(true);
            setTimeout(() => setLightningFlash(false), 60);
          }
        }, 130);
      }
    }, 9000);

    return () => clearInterval(interval);
  }, [weather]);

  // Rain, Pollen & Atmospheric Particles Physics Animation Loop
  useEffect(() => {
    const isRaining = weather === 'rainy';
    const isPollenActive =
      weather === 'sunny' &&
      (timeOfDay === 'day' || timeOfDay === 'dawn' || timeOfDay === 'sunset');

    // If neither effect is active, skip running the canvas loop to save 100% CPU/GPU
    if (!isRaining && !isPollenActive) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = canvas.parentElement?.clientWidth || window.innerWidth);
    let height = (canvas.height = canvas.parentElement?.clientHeight || window.innerHeight);

    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!canvas || !canvas.parentElement) return;
        width = canvas.width = canvas.parentElement.clientWidth;
        height = canvas.height = canvas.parentElement.clientHeight;
      }, 100);
    };

    window.addEventListener('resize', handleResize, { passive: true });

    // 1. Raindrop particles
    const raindrops: Array<{
      x: number;
      y: number;
      length: number;
      speed: number;
      opacity: number;
    }> = isRaining
      ? Array.from({ length: 85 }).map(() => ({
          x: Math.random() * width,
          y: Math.random() * height,
          length: 12 + Math.random() * 16,
          speed: 14 + Math.random() * 12,
          opacity: 0.35 + Math.random() * 0.45,
        }))
      : [];

    // Rain Splash Ripples on ground
    const splashes: Array<{
      x: number;
      y: number;
      radius: number;
      maxRadius: number;
      opacity: number;
    }> = [];

    // 2. Pollen / Sun dust particles (Sunny Day/Dawn/Sunset)
    const pollenParticles: Array<{
      x: number;
      y: number;
      radius: number;
      vx: number;
      vy: number;
      alpha: number;
      pulse: number;
    }> = isPollenActive
      ? Array.from({ length: 22 }).map(() => ({
          x: Math.random() * width,
          y: Math.random() * (height * 0.75),
          radius: 1 + Math.random() * 2,
          vx: 0.15 + Math.random() * 0.3,
          vy: -0.1 + Math.random() * 0.2,
          alpha: 0.3 + Math.random() * 0.5,
          pulse: Math.random() * Math.PI * 2,
        }))
      : [];

    // Render loop
    const render = () => {
      if (document.hidden) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      ctx.clearRect(0, 0, width, height);

      // A. RENDER RAIN & SPLASHES
      if (isRaining) {
        ctx.strokeStyle = 'rgba(200, 230, 255, 0.65)';
        ctx.lineWidth = 1.2;
        ctx.lineCap = 'round';

        raindrops.forEach((drop) => {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(215, 238, 255, ${drop.opacity})`;
          ctx.moveTo(drop.x, drop.y);
          // 12 degree wind slant
          ctx.lineTo(drop.x - 2.5, drop.y + drop.length);
          ctx.stroke();

          // Move Drop
          drop.y += drop.speed;
          drop.x -= 2.5;

          // Hit ground
          if (drop.y > height - 45) {
            // Spawn Splash Ripple
            if (splashes.length < 30 && Math.random() < 0.35) {
              splashes.push({
                x: drop.x,
                y: height - 30 + (Math.random() * 25 - 12),
                radius: 1,
                maxRadius: 4 + Math.random() * 6,
                opacity: 0.6,
              });
            }
            drop.y = -drop.length - Math.random() * 40;
            drop.x = Math.random() * (width + 50);
          }
        });

        // Render & Update Splashes
        for (let i = splashes.length - 1; i >= 0; i--) {
          const s = splashes[i];
          ctx.beginPath();
          ctx.ellipse(s.x, s.y, s.radius * 1.6, s.radius * 0.7, 0, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(210, 235, 255, ${s.opacity})`;
          ctx.lineWidth = 0.9;
          ctx.stroke();

          s.radius += 0.45;
          s.opacity -= 0.045;

          if (s.opacity <= 0 || s.radius >= s.maxRadius) {
            splashes.splice(i, 1);
          }
        }
      }

      // B. RENDER SUN DUST / GOLDEN POLLEN (Sunny Daytime / Dawn / Sunset)
      if (isPollenActive) {
        pollenParticles.forEach((p) => {
          p.pulse += 0.03;
          const currentAlpha = p.alpha * (0.6 + 0.4 * Math.sin(p.pulse));

          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.fillStyle =
            timeOfDay === 'sunset'
              ? `rgba(255, 200, 100, ${currentAlpha})`
              : timeOfDay === 'dawn'
              ? `rgba(255, 220, 170, ${currentAlpha})`
              : `rgba(255, 255, 210, ${currentAlpha})`;
          ctx.shadowBlur = 4;
          ctx.shadowColor = timeOfDay === 'sunset' ? '#F97316' : '#FDE047';
          ctx.fill();
          ctx.shadowBlur = 0;

          // Drift physics
          p.x += p.vx;
          p.y += p.vy + Math.sin(p.pulse) * 0.15;

          if (p.x > width + 10) p.x = -10;
          if (p.y < 0) p.y = height * 0.7;
          if (p.y > height * 0.8) p.y = 20;
        });
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [timeOfDay, weather]);

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-15">
      {/* Lightning Flash Backdrop */}
      {lightningFlash && (
        <div className="absolute inset-0 bg-white/40 z-30 pointer-events-none transition-opacity" />
      )}

      {/* Physics Canvas for Rain, Splashes, and Pollen */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-20"
      />
    </div>
  );
});

GardenWeatherEffects.displayName = 'GardenWeatherEffects';
